/**
 * The DVM Layout Builder's build form for ONE Design Builder widget.
 *
 * A standalone implementation of `DvmBuilderHost`: it hosts the Layout
 * Builder's own step components (bar, KPI, drill-down, inputs, …) so the
 * form, payloads and Generate are identical, while the "dashboard" those
 * steps see is the Design Builder canvas (via `DesignRuntimeService`).
 * Config look-ups delegate to the shared `dvm-builder-config.utils`.
 *
 * Always mounted (inside the Data tab): the runtime also uses its steps to
 * apply recompute results, exactly as the Layout Builder does.
 */

import { locateNode } from '../utils/design-node.utils';
import { inputDataBindingOf } from '../utils/design-input-data.utils';
import { CommonModule } from '@angular/common';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import {
  ChangeDetectorRef, Component, DestroyRef, EventEmitter, Input, NgZone, OnDestroy, OnInit, Output, ViewChild,
  ViewEncapsulation, forwardRef, inject,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NgSelectModule } from '@ng-select/ng-select';
import { SsSelectTooltipDirective } from '../../shared/ss-select-tooltip.directive';

import { SelfServiceConfigService } from '../../shared/self-service-config.service';
import { SelfServiceDatasetService } from '../../shared/self-service-dataset.service';
import { SelfServiceUiService } from '../../shared/self-service-ui.service';
import { SelfServiceChartPersistenceService } from '../../shared/self-service-chart-persistence.service';
import { SelfServiceDashboardService } from '../../shared/self-service-dashboard.service';
import { layoutHasNavTargets, navTargetComponentOptionsFromLayout } from '../../shared/nav-target.utils';
import { KeycloakService } from 'keycloak-angular';
import { resolveSelfServiceApiUrl } from '../../shared/self-service-api.utils';
import { resolveRecomputeChartType } from '../../shared/self-service-recompute.utils';
import { readJson } from '../../shared/self-service-storage.utils';
import type { SavedSelfServiceDashboard } from '../../shared/self-service-types';
import type {
  BuilderLayoutConfig, CapabilityRegistryEntry, FormatControl, FormatControlGroup, FormulaBuilderConfig, LabelValueOption,
} from '../../shared/self-service-config.types';
import { DvmBuilderHost, type DvmBuilderStep } from '../../dvm-layout-builder/dvm-builder-host';
import type { DashboardTile, InputApiCallConfig, TargetFormulaReferenceEntry } from '../../dvm-layout-builder/dvm-layout-builder.types';
import {
  DVM_LAYOUT_BUILDER_DEFAULT_LAYOUT, applicableFormatControls, capabilitiesForType, categoryOfType, chartExposesSection, chartSupports,
  componentIdPrefixFor, controlVisibleForSubType, fieldEnabled, fieldLabel, fontWeightOptionsOf, formatControlVisible, formatControlsOf,
  formatGroupOrderOf, formulaBuilderConfigOf, formulaReferencesEnabledFor, groupFormatControls, layoutSectionHint, layoutSectionTitle,
  measureAggregationsOf, numberFormatsOf, resolvePaletteColors,
} from '../../dvm-layout-builder/dvm-builder-config.utils';
import { API_CALL_MODE_OPTIONS, WIDGET_API_CALL_MODE_OPTIONS, defaultApiCallConfig, sanitizeApiCallConfig, restoreApiCallConfig } from '../../dvm-layout-builder/dvm-input-api-call.utils';
import { finalize, map, tap } from 'rxjs';
import { DesignWidgetApiService, widgetApiResult } from './design-widget-api.service';
import { RelationHopsComponent } from '../../dvm-layout-builder/relation-hops.component';
import { copyRelationHops, type RelationHop, type RelationHopsConfig } from '../../dvm-layout-builder/relation-hops.types';
import { PayloadTemplateModalComponent, hasPayloadTemplate, payloadTemplatesOn, type PayloadTemplateModalRequest } from '../../dvm-layout-builder/payload-template-modal.component';
import type { TableMatrixPayloadTemplate } from '../../../chart-components/unified-table-matrix-chart/unified-table-matrix-chart.types';
import { waterfallTileFromResponse } from '../../components/waterfall-step/waterfall-tile.utils';
import { KpiStepComponent } from '../../components/kpi-step/kpi-step.component';
import { FileUploadStepComponent } from '../../components/file-upload-step/file-upload-step.component';
import { InputControlStepComponent } from '../../components/input-control-step/input-control-step.component';
import { DropdownStepComponent } from '../../components/dropdown-step/dropdown-step.component';
import { PopupStepComponent } from '../../components/popup-step/popup-step.component';
import { HorizontalBarStepComponent } from '../../components/horizontal-bar-step/horizontal-bar-step.component';
import { WaterfallStepComponent } from '../../components/waterfall-step/waterfall-step.component';
import { ScatterBubbleStepComponent } from '../../components/scatter-bubble-step/scatter-bubble-step.component';
import { PieCompositionStepComponent } from '../../components/pie-composition-step/pie-composition-step.component';
import { BarColumnStepComponent } from '../../components/bar-column-step/bar-column-step.component';
import { LineAreaStepComponent } from '../../components/line-area-step/line-area-step.component';
import { DrillDownStepComponent } from '../../components/drill-down-step/drill-down-step.component';
import { TableMatrixStepComponent } from '../../components/table-matrix-step/table-matrix-step.component';
import { NestedTableStepComponent } from '../../components/nested-table-step/nested-table-step.component';
import type { DesignNode } from '../design-builder.types';
import { FALLBACK_LAYOUT_TYPES } from '../design-builder.constants';
import { DesignRuntimeService, TEXT_TARGET_PREFIX, textTargetsOf, type RecomputeApplier } from './design-runtime.service';
import { DesignDocumentStore } from '../state/design-document.store';
import { widgetNodes } from './design-tiles.utils';
import { withSampleData } from '../renderers/design-sample-data';
import { defaultAppearance } from '../renderers/design-appearance.spec';
import { DesignDataService, TEXT_AGGREGATIONS } from '../services/design-data.service';

const FIELD_DRAG_TYPE = 'application/x-dvm-field';
const SAVED_DASHBOARDS_STORAGE_KEY = 'dvm.selfService.savedDashboards';
const LEGACY_GENERATE_PATH = '/api/dvm/self-service/waterfall/generate';

@Component({
  selector: 'app-design-build-host',
  standalone: true,
  imports: [
    CommonModule, FormsModule, MatButtonModule, MatIconModule, MatTooltipModule, NgSelectModule, SsSelectTooltipDirective, RelationHopsComponent,
    PayloadTemplateModalComponent, KpiStepComponent, FileUploadStepComponent, InputControlStepComponent, DropdownStepComponent,
    PopupStepComponent, HorizontalBarStepComponent, WaterfallStepComponent, ScatterBubbleStepComponent, PieCompositionStepComponent, BarColumnStepComponent,
    LineAreaStepComponent, DrillDownStepComponent, TableMatrixStepComponent, NestedTableStepComponent,],
  templateUrl: './design-build-host.component.html',
  // The Layout Builder's sheet styles the steps identically; the second only
  // fits the panel into a side pane. Both are scoped under the host classes.
  styleUrls: ['../../dvm-layout-builder/dvm-layout-builder.component.scss', './design-build-host.component.scss'],
  encapsulation: ViewEncapsulation.None,
  host: { class: 'dvm-layout-builder-root dsg-build-host' },
  providers: [{ provide: DvmBuilderHost, useExisting: forwardRef(() => DesignBuildHostComponent) }],
})
export class DesignBuildHostComponent implements DvmBuilderHost, RecomputeApplier, OnInit, OnDestroy {
  private readonly http = inject(HttpClient);
  private readonly widgetApi = inject(DesignWidgetApiService);
  private readonly ui = inject(SelfServiceUiService);
  private readonly configService = inject(SelfServiceConfigService);
  private readonly datasetService = inject(SelfServiceDatasetService);
  private readonly persistence = inject(SelfServiceChartPersistenceService);
  private readonly dashboardService = inject(SelfServiceDashboardService);
  /** Optional: the host also runs in tests / previews without an auth context. */
  private readonly keycloak = inject(KeycloakService, { optional: true });
  private readonly runtime = inject(DesignRuntimeService);
  private readonly doc = inject(DesignDocumentStore);
  private readonly dataService = inject(DesignDataService);

  /** The selected widget node (null hides the form; the steps stay alive). */
  @Input() set node(value: DesignNode | null) {
    this.selected = value?.type === 'WIDGET' && value.widget ? value : null;
    // Re-typed elsewhere (the Design tab's Table type): reopen the form for the new type.
    if (this.selected && this.selected.id === this.loadedNodeId && this.selected.widget!.layoutType !== this.selectedLayoutType) {
      this.loadedNodeId = '';
    }
    if (this.selected && this.selected.id === this.loadedNodeId) this.syncTextTargets();
    this.loadSelected();
  }
  /**
   * Only lend the family steps' recompute appliers (a published dashboard):
   * the form never shows, so neither the builder config nor the dataset list
   * is fetched.
   */
  @Input() engineOnly = false;
  /** A tile to save onto the widget: Generate / Update produced it. */
  @Output() readonly built = new EventEmitter<{ nodeId: string; tile: DashboardTile }>();

  @ViewChild(KpiStepComponent, { static: true }) private kpiStep!: KpiStepComponent;
  @ViewChild(FileUploadStepComponent, { static: true }) private fileUploadStep!: FileUploadStepComponent;
  @ViewChild(InputControlStepComponent, { static: true }) private inputControlStep!: InputControlStepComponent;
  @ViewChild(DropdownStepComponent, { static: true }) private dropdownStep!: DropdownStepComponent;
  @ViewChild(PopupStepComponent, { static: true }) private popupStep!: PopupStepComponent;
  @ViewChild(HorizontalBarStepComponent, { static: true }) private horizontalBarStep!: HorizontalBarStepComponent;
  @ViewChild(WaterfallStepComponent, { static: true }) private waterfallStep!: WaterfallStepComponent;
  @ViewChild(ScatterBubbleStepComponent, { static: true }) private scatterBubbleStep!: ScatterBubbleStepComponent;
  @ViewChild(PieCompositionStepComponent, { static: true }) private pieCompositionStep!: PieCompositionStepComponent;
  @ViewChild(BarColumnStepComponent, { static: true }) private barColumnStep!: BarColumnStepComponent;
  @ViewChild(LineAreaStepComponent, { static: true }) private lineAreaStep!: LineAreaStepComponent;
  @ViewChild(DrillDownStepComponent, { static: true }) drillDownStep!: DrillDownStepComponent;
  @ViewChild(TableMatrixStepComponent, { static: true }) private tableMatrixStep!: TableMatrixStepComponent;
  @ViewChild(NestedTableStepComponent, { static: true }) private nestedTableStep!: NestedTableStepComponent;

  // ── State ──────────────────────────────────────────────────────────────────
  layout: BuilderLayoutConfig = DVM_LAYOUT_BUILDER_DEFAULT_LAYOUT;
  layoutReady = false;
  selected: DesignNode | null = null;
  private loadedNodeId = '';

  selectedLayoutType = '';
  title = '';
  componentId = '';
  dashboardId: number | null = null;
  componentType = 'CHART';
  editingTileId: string | null = null;
  previewTileId: string | null = null;
  generateError = '';
  isGenerating = false;

  selectedDatasets: string[] = [];
  entityOptions: string[] = [];
  entitiesLoading = false;
  private entitiesLoadFailed = false;
  private readonly columnsCache = new Map<string, string[]>();
  /** Same, without the `hiddenColumns` filter. */
  private readonly allColumnsCache = new Map<string, string[]>();
  private readonly rowsCache = new Map<string, Array<Record<string, unknown>>>();
  private columnsLoading: Record<string, boolean> = {};
  private columnsError: Record<string, string> = {};
  private readonly rowColumnsMemo = new Map<string, { dataset: string; source: string[] | undefined; result: string[] }>();

  barFormat: Record<string, unknown> = {};
  private openFormatGroups = new Set<string>();
  private formatGroupsMemo: { key: string; groups: FormatControlGroup[] } | null = null;
  payloadTemplateModal: PayloadTemplateModalRequest | null = null;

  selectedTargetCharts: string[] = [];
  interactionsSectionOpen = false;
  buildApiCallConfig: InputApiCallConfig = defaultApiCallConfig();
  /** Rows of the custom-API "mode" picker, worded for a control or a widget. */
  apiCallModeOptions(kind: string): { value: InputApiCallConfig['mode']; label: string }[] {
    return kind === 'widget' ? WIDGET_API_CALL_MODE_OPTIONS : API_CALL_MODE_OPTIONS;
  }
  targetFormulas: Record<string, string> = {};
  targetStepFormulas: Record<string, Record<string, string>> = {};
  targetFormulaReferences: Record<string, TargetFormulaReferenceEntry[]> = {};
  relationHops: RelationHop[] = [];

  /** Step writes are saved to the widget once it has been generated (or while generating). */
  private persistWrites = false;
  private componentCounter = 0;

  constructor() {
    // The form sits under OnPush panes: async loads / Generate responses would
    // otherwise never repaint it. Re-check just this subtree when the app settles.
    const changeDetector = inject(ChangeDetectorRef);
    const sub = inject(NgZone).onStable.subscribe(() => changeDetector.detectChanges());
    inject(DestroyRef).onDestroy(() => sub.unsubscribe());
  }

  ngOnInit(): void {
    this.runtime.attach(this);
    if (this.engineOnly) return;
    this.configService.loadBuilderLayout('dvm-layout', DVM_LAYOUT_BUILDER_DEFAULT_LAYOUT).subscribe((layout) => {
      this.layout = layout;
      this.componentType = layout.defaults?.componentType || 'CHART';
      this.dashboardId = typeof layout.defaults?.dashboardId === 'number' ? layout.defaults.dashboardId : null;
      this.formatGroupsMemo = null;
      this.layoutReady = true;
      this.reloadDatasets();
      this.loadedNodeId = '';
      this.loadSelected();
    });
    this.loadApiTargetDashboards();
  }

  ngOnDestroy(): void {
    this.runtime.attach(null);
  }

  /** PUBLISHED dashboards this user owns (dashboard_layouts, by email) — the
   *  Navigation accordion's "Target dashboard" list. Same call as the Layout
   *  Builder; this browser's saved dashboards are merged in below. */
  private apiTargetDashboards: Array<{ label: string; value: string; layoutJson: Record<string, unknown> }> = [];
  private async loadApiTargetDashboards(): Promise<void> {
    let email = '';
    try {
      if (this.keycloak && (await this.keycloak.isLoggedIn())) email = (await this.keycloak.loadUserProfile()).email || '';
    } catch {
      // No auth context → localStorage list only.
    }
    if (!email) return;
    this.dashboardService.fetchOwnedDashboardsFromApi(email).subscribe({
      next: (owned) => {
        this.apiTargetDashboards = (owned || []).map((d) => ({
          label: String((d.layoutJson as Record<string, unknown>)?.['dashboard-name'] ?? d.name ?? d.slug).trim() || d.slug,
          value: d.slug,
          layoutJson: (d.layoutJson || {}) as Record<string, unknown>,
        }));
        this.navMemo = null;
        this.navComponentsMemo = null;
      },
      error: () => { /* API unavailable — fall back to the localStorage list */ },
    });
  }

  // ── Steps ──────────────────────────────────────────────────────────────────

  private stepFor(layoutType: string): DvmBuilderStep | null {
    switch (layoutType) {
      case 'KPI': return this.kpiStep;
      case 'FILE_UPLOAD': return this.fileUploadStep;
      case 'INPUT_BOX':
      case 'SLIDER': return this.inputControlStep;
      case 'DROPDOWN': return this.dropdownStep;
      case 'POPUP': return this.popupStep;
      case 'HORIZONTAL_BAR_CHART': return this.horizontalBarStep;
      case 'WATERFALL': return this.waterfallStep;
      case 'SCATTER_BUBBLE': return this.scatterBubbleStep;
      case 'PIE_COMPOSITION': return this.pieCompositionStep;
      case 'BAR': return this.barColumnStep;
      case 'LINE_AREA': return this.lineAreaStep;
      case 'DRILL_DOWN_TABLE': return this.drillDownStep;
      case 'TABLE_MATRIX': return this.tableMatrixStep;
      case 'NESTED_TABLE': return this.nestedTableStep;
      default: return null;
    }
  }

  private get activeStep(): DvmBuilderStep | null { return this.stepFor(this.selectedLayoutType); }
  private get allSteps(): DvmBuilderStep[] {
    return [this.kpiStep, this.fileUploadStep, this.inputControlStep, this.dropdownStep, this.popupStep, this.horizontalBarStep,
      this.waterfallStep, this.scatterBubbleStep, this.pieCompositionStep, this.barColumnStep, this.lineAreaStep, this.drillDownStep, this.tableMatrixStep, this.nestedTableStep]
      .filter((step): step is NonNullable<typeof step> => !!step);
  }

  /** RecomputeApplier: the owning family applies a recompute result, as in the Layout Builder. */
  applyRecomputeResult(tile: DashboardTile, entry: Record<string, unknown>): boolean {
    const result = (entry?.['result'] ?? entry) as Record<string, unknown>;
    const isInput = tile.layoutType === 'DROPDOWN' || tile.layoutType === 'SLIDER' || tile.layoutType === 'INPUT_BOX';
    const family = resolveRecomputeChartType(entry) === 'INPUT' && isInput ? tile.layoutType : String(result?.['chartType'] ?? tile.layoutType ?? '');
    const format = tile.format || this.defaultTileFormat(tile.layoutType);
    return this.stepFor(family)?.applyRecomputeResult?.(tile, { entry, result, format }) ?? false;
  }

  // ── Layout type helpers (template + steps) ─────────────────────────────────
  get isWaterfallLayout(): boolean { return this.selectedLayoutType === 'WATERFALL'; }
  get isBarLayout(): boolean { return this.selectedLayoutType === 'BAR'; }
  get isLineAreaLayout(): boolean { return this.selectedLayoutType === 'LINE_AREA'; }
  get isScatterBubbleLayout(): boolean { return this.selectedLayoutType === 'SCATTER_BUBBLE'; }
  get isPieCompositionLayout(): boolean { return this.selectedLayoutType === 'PIE_COMPOSITION'; }
  get isTableMatrixLayout(): boolean { return this.selectedLayoutType === 'TABLE_MATRIX'; }
  get isChartTargetLayout(): boolean {
    return !['INPUT_BOX', 'SLIDER', 'DROPDOWN', 'FILE_UPLOAD', 'POPUP'].includes(this.selectedLayoutType);
  }
  get buildPanelTitle(): string {
    return this.layout.options?.layoutTypes?.find((type) => type.value === this.selectedLayoutType)?.label ?? this.selectedLayoutType;
  }
  get tmType(): string { return this.tableMatrixStep?.tmType ?? ''; }
  get isTmDrilldown(): boolean { return this.tableMatrixStep?.isTmDrilldown ?? false; }
  addWaterfallStep(): void { this.waterfallStep.addStep(); }

  get activeRelationHops(): RelationHopsConfig | null {
    const config = this.layout.relationHops;
    return config?.enabled && config.dropdowns?.length && config.categoryIds.includes(categoryOfType(this.layout, this.selectedLayoutType))
      ? config : null;
  }

  // ── Config (dvm-builder-config.utils) ──────────────────────────────────────
  get activeCapabilities(): CapabilityRegistryEntry { return capabilitiesForType(this.layout, this.selectedLayoutType); }
  chartSupports(capability: string): boolean { return chartSupports(this.layout, this.selectedLayoutType, capability); }
  chartExposesSection(section: string): boolean { return chartExposesSection(this.layout, this.selectedLayoutType, section); }
  layoutSectionTitle(id: string, fallback: string): string { return layoutSectionTitle(this.layout, this.selectedLayoutType, id, fallback); }
  layoutSectionHint(id: string, fallback = ''): string { return layoutSectionHint(this.layout, this.selectedLayoutType, id, fallback); }
  fieldEnabled(id: string, key: string): boolean { return fieldEnabled(this.layout, this.selectedLayoutType, id, key); }
  fieldLabel(id: string, key: string, fallback: string): string { return fieldLabel(this.layout, this.selectedLayoutType, id, key, fallback); }
  get fontWeightOptions(): LabelValueOption[] { return fontWeightOptionsOf(this.layout); }
  get measureAggregations(): LabelValueOption[] { return measureAggregationsOf(this.layout); }
  get numberFormats(): LabelValueOption[] { return numberFormatsOf(this.layout); }
  get formatControls(): FormatControl[] { return formatControlsOf(this.layout); }
  get formulaBuilderConfig(): FormulaBuilderConfig { return formulaBuilderConfigOf(this.layout); }
  get formatGroupOrder(): string[] { return formatGroupOrderOf(this.layout); }
  applicableFormatControls(layoutType: string): FormatControl[] { return applicableFormatControls(this.layout, layoutType); }
  formatControlVisible(control: FormatControl, format: Record<string, unknown> | undefined): boolean { return formatControlVisible(control, format); }
  resolvePaletteColors(paletteKey?: string): string[] | undefined { return resolvePaletteColors(this.layout, paletteKey); }
  formulaReferencesEnabledFor(layoutType: string): boolean { return formulaReferencesEnabledFor(this.layout, layoutType); }

  formatControlOptions(control: FormatControl): LabelValueOption[] {
    if (control.control === 'column-select' || control.optionsSource === 'datasetColumns') {
      return this.builderColumns.map((column) => ({ label: column, value: column }));
    }
    const list = control.optionsKey ? (this.layout.options as Record<string, unknown> | undefined)?.[control.optionsKey] : undefined;
    return Array.isArray(list) ? (list as LabelValueOption[]) : [];
  }

  defaultTileFormat(layoutType: string): Record<string, unknown> {
    const format: Record<string, unknown> = {};
    const subType = layoutType === 'TABLE_MATRIX' ? this.tmType : '';
    this.applicableFormatControls(layoutType).forEach((control) => {
      if (subType && !controlVisibleForSubType(control, subType)) return;
      if (control.default !== undefined) format[control.key] = control.default;
    });
    return format;
  }

  get barFormatGroups(): FormatControlGroup[] {
    const key = `${this.selectedLayoutType}|${this.isTableMatrixLayout ? this.tmType : ''}`;
    if (this.formatGroupsMemo?.key !== key) {
      const applicable = this.applicableFormatControls(this.selectedLayoutType)
        .filter((control) => controlVisibleForSubType(control, this.isTableMatrixLayout ? this.tmType : ''));
      this.formatGroupsMemo = { key, groups: groupFormatControls(applicable, this.formatGroupOrder, (name) => this.chartExposesSection(name)) };
    }
    return this.formatGroupsMemo.groups;
  }
  isBarFormatGroupOpen(group: string): boolean { return this.openFormatGroups.has(group); }
  toggleBarFormatGroup(group: string): void {
    const next = new Set(this.openFormatGroups);
    if (!next.delete(group)) next.add(group);
    this.openFormatGroups = next;
  }

  onBarFormatChange(): void {
    const titleFromFormat = this.isBarLayout || this.isLineAreaLayout || this.isWaterfallLayout || (this.isTableMatrixLayout && this.isTmDrilldown);
    if (titleFromFormat && typeof this.barFormat['title'] === 'string') this.title = this.barFormat['title'] as string;
    const step = this.activeStep;
    if (step?.onFormatChanged) { step.onFormatChanged(); return; }
    const tile = this.editingTileId ? this.findTileAnywhere(this.editingTileId) : undefined;
    if (tile) this.onTileFormatChange({ ...tile, format: { ...(tile.format || {}), ...this.barFormat } });
  }

  hasPayloadTemplate(format: Record<string, unknown> | undefined, key: string): boolean { return hasPayloadTemplate(format, key); }
  openPayloadTemplateModalForBar(control: FormatControl): void {
    this.payloadTemplateModal = {
      endpointKey: control.key, endpointLabel: control.label,
      template: payloadTemplatesOn(this.barFormat)[control.key], columns: this.builderColumns,
    };
  }
  closePayloadTemplateModal(): void { this.payloadTemplateModal = null; }
  onPayloadTemplateSaved(template: TableMatrixPayloadTemplate | undefined): void {
    const key = this.payloadTemplateModal?.endpointKey;
    if (key) {
      const templates = { ...payloadTemplatesOn(this.barFormat) };
      if (template) templates[key] = template; else delete templates[key];
      this.barFormat['payloadTemplates'] = Object.keys(templates).length ? templates : undefined;
      this.onBarFormatChange();
    }
    this.closePayloadTemplateModal();
  }

  // ── Datasets & columns (same SelfServiceDatasetService calls) ──────────────
  private get entityGroup(): string { return this.layout.options?.entityGroup?.trim() || 'NontimeSeriesEntities'; }
  get drillDataset(): string { return this.selectedDatasets[0] || ''; }
  get drillDatasetColumns(): string[] { return this.columnsCache.get(this.drillDataset) || []; }
  get isDrillDatasetColumnsLoading(): boolean { return this.isDatasetColumnsLoading(this.drillDataset); }
  get drillColumnsErrorMessage(): string { return this.columnsError[this.drillDataset] || ''; }
  get builderColumns(): string[] { return this.isBarLayout ? this.barColumnStep.barColumns : this.drillDatasetColumns; }
  isDatasetColumnsLoading(dataset: string): boolean { return !!dataset && !!this.columnsLoading[dataset]; }
  cachedColumns(dataset: string): string[] | undefined { return this.columnsCache.get(dataset); }
  cachedRows(dataset: string): Array<Record<string, unknown>> | undefined { return this.rowsCache.get(dataset); }
  forgetRowColumns(rowId: string): void { this.rowColumnsMemo.delete(rowId); }
  columnPlaceholder(): string {
    if (!this.drillDataset) return 'Select a dataset first';
    return this.isDrillDatasetColumnsLoading ? 'Loading columns...' : 'Select column';
  }
  drillColumnPlaceholder(kind: 'dimension' | 'measure'): string {
    if (!this.drillDataset) return 'Select a dataset first';
    if (this.isDrillDatasetColumnsLoading) return 'Loading columns...';
    if (this.drillColumnsErrorMessage) return 'Unable to load columns';
    return kind === 'dimension' ? 'Select group-by column' : 'Select value column';
  }
  columnsForRow(rowId: string, dataset: string): string[] {
    const source = dataset ? this.columnsCache.get(dataset) : undefined;
    const cached = this.rowColumnsMemo.get(rowId);
    if (cached && cached.dataset === dataset && cached.source === source) return cached.result;
    const result = source || [];
    this.rowColumnsMemo.set(rowId, { dataset, source, result });
    return result;
  }
  /** Like columnsForRow, but with the columns `hiddenColumns` leaves out (uuid, system attributes). */
  allColumnsForRow(rowId: string, dataset: string): string[] {
    const source = dataset ? this.allColumnsCache.get(dataset) : undefined;
    const key = `all:${rowId}`;
    const cached = this.rowColumnsMemo.get(key);
    if (cached && cached.dataset === dataset && cached.source === source) return cached.result;
    const result = source || [];
    this.rowColumnsMemo.set(key, { dataset, source, result });
    return result;
  }

  get datasetStatus(): { kind: 'error' | 'hint' | 'none'; text: string; canRetry: boolean } {
    if (this.entitiesLoading) return { kind: 'hint', text: 'Loading datasets…', canRetry: false };
    if (this.entitiesLoadFailed) return { kind: 'error', text: 'Could not load the dataset list. Check your connection and try again.', canRetry: true };
    if (!this.entityOptions.length) return { kind: 'error', text: 'No datasets found. Create one in the Data Builder first, then reload.', canRetry: true };
    if (!this.drillDataset) return { kind: 'hint', text: 'Pick a dataset to enable the field pickers and Generate.', canRetry: false };
    if (this.isDrillDatasetColumnsLoading) return { kind: 'hint', text: `Loading columns from "${this.drillDataset}"…`, canRetry: false };
    if (this.drillColumnsErrorMessage) return { kind: 'error', text: this.drillColumnsErrorMessage, canRetry: false };
    return { kind: 'none', text: '', canRetry: false };
  }

  reloadDatasets(): void {
    this.entitiesLoading = true;
    this.entitiesLoadFailed = false;
    this.datasetService.getEntities(this.entityGroup).subscribe({
      next: (entities) => { this.entityOptions = entities; this.entitiesLoading = false; },
      error: () => { this.entityOptions = []; this.entitiesLoading = false; this.entitiesLoadFailed = true; },
    });
  }

  onSelectedDatasetsChange(datasets: string[] | null): void {
    const previous = this.drillDataset;
    this.selectedDatasets = datasets || [];
    this.selectedDatasets.forEach((dataset) => this.loadColumnsForDataset(dataset));
    if (this.drillDataset && this.drillDataset !== previous) this.activeStep?.onDatasetChanged?.(this.drillDataset, previous);
    this.activeStep?.onDatasetsChanged?.();
  }

  loadColumnsForDataset(dataset: string): void {
    if (!dataset) return;
    if (this.columnsCache.has(dataset)) { this.loadRows(dataset); return; }
    this.columnsLoading = { ...this.columnsLoading, [dataset]: true };
    delete this.columnsError[dataset];
    this.datasetService.getEntityAttributes(dataset, this.entityGroup).subscribe({
      next: (attributes) => {
        const hidden = new Set((this.layout.options?.hiddenColumns ?? ['uuid', '__rowKey']).map((name) => name.toLowerCase()));
        const columns = [...new Set(attributes)].filter((name) => !hidden.has(name.toLowerCase()));
        this.columnsCache.set(dataset, columns);
        // Every column incl. uuid and the other system attributes - for builders that
        // address individual records (nested table: ID columns for row save / approval).
        this.allColumnsCache.set(dataset, [...new Set(attributes)].filter((name) => name !== '__rowKey'));
        this.columnsLoading = { ...this.columnsLoading, [dataset]: false };
        if (!columns.length) this.columnsError[dataset] = `"${dataset}" returned no columns — check the entity name or its metadata.`;
        this.activeStep?.onDatasetColumnsLoaded?.(dataset);
        this.loadRows(dataset);
      },
      error: (error: HttpErrorResponse) => {
        this.columnsLoading = { ...this.columnsLoading, [dataset]: false };
        this.columnsError[dataset] = error.error?.message || error.message || `Unable to load columns for "${dataset}".`;
      },
    });
  }

  private loadRows(dataset: string): void {
    if (this.rowsCache.has(dataset)) return;
    this.datasetService.getEntityRows(dataset).subscribe({
      next: (rows) => {
        const list = Array.isArray(rows) ? rows : [];
        this.rowsCache.set(dataset, list);
        this.addSystemColumns(dataset, list);
        if (this.drillDataset === dataset) this.activeStep?.onDatasetRowsLoaded?.(dataset);
      },
      error: () => this.rowsCache.set(dataset, []),
    });
  }

  /**
   * `getEntityAttributes` lists only the entity's own attributes; the system
   * attributes (created_by, updated_by, approval fields …) come only on the
   * rows. Add them to the column pickers so a table can show them - or filter
   * on them ("Approver = {userEmail}"). Internal fields stay hidden.
   */
  private addSystemColumns(dataset: string, rows: Array<Record<string, unknown>>): void {
    const all = this.allColumnsCache.get(dataset);
    const visible = this.columnsCache.get(dataset);
    if (!all || !visible || !rows.length) return;
    const known = new Set(all.map((name) => name.toLowerCase()));
    const extra: string[] = [];
    for (const row of rows.slice(0, 50)) {
      for (const key of Object.keys(row ?? {})) {
        const lower = key.toLowerCase();
        if (key === '__rowKey' || known.has(lower)) continue;
        known.add(lower);
        extra.push(key);
      }
    }
    if (!extra.length) return;
    const hidden = new Set((this.layout.options?.hiddenColumns ?? ['uuid', '__rowKey']).map((name) => name.toLowerCase()));
    this.allColumnsCache.set(dataset, [...all, ...extra]);
    const shown = extra.filter((name) => !hidden.has(name.toLowerCase()));
    if (!shown.length) return;
    this.columnsCache.set(dataset, [...visible, ...shown]);
    if (!visible.length) delete this.columnsError[dataset];
    this.activeStep?.onDatasetColumnsLoaded?.(dataset);
  }

  // ── Drag & drop of a field onto a form control ─────────────────────────────
  allowFieldDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    if (event.dataTransfer) event.dataTransfer.dropEffect = 'copy';
  }
  readFieldDrop(event: DragEvent): { dataset: string; field: string } | null {
    event.preventDefault();
    event.stopPropagation();
    try {
      const mapping = JSON.parse(event.dataTransfer?.getData(FIELD_DRAG_TYPE) || 'null') as { dataset?: string; field?: string } | null;
      return mapping?.dataset && mapping.field ? { dataset: mapping.dataset, field: mapping.field } : null;
    } catch {
      return null;
    }
  }
  useDroppedSingleDataset(dataset: string): void {
    this.onSelectedDatasetsChange([dataset, ...this.selectedDatasets.filter((item) => item !== dataset)]);
  }
  appendFormulaToken(current: string, field: string): string {
    const trimmed = (current || '').trimEnd();
    return trimmed ? `${trimmed} [${field}]` : `[${field}]`;
  }

  // ── Targets: the OTHER generated widgets on the canvas ─────────────────────
  private targetsMemo: { key: string; options: Array<LabelValueOption & { disabled?: boolean }> } | null = null;
  /**
   * Every OTHER component on the canvas. Generated ones are selectable; one
   * not generated yet has no componentId to call, so it is listed disabled
   * rather than silently missing.
   *
   * Labels name the component, not just its type: tile titles default to the
   * type label, so two dropdowns both read "Dropdown (Input)".
   */
  get targetCharts(): Array<LabelValueOption & { disabled?: boolean }> {
    const skip = (id: string, layoutType: string) => id === this.selected?.id || layoutType === 'POPUP' || layoutType === 'BUTTON';
    const tiles = new Map(this.dashboardTiles.map((tile) => [tile.id, tile]));
    const nodes = widgetNodes(this.doc.document()).filter((node) => !skip(node.id, node.widget!.layoutType));
    // Tiles with no node of their own (rare) still count.
    const orphanTiles = [...tiles.values()].filter((tile) => !skip(tile.id, tile.layoutType) && !nodes.some((node) => node.id === tile.id));

    // Selectable = has a componentId (what the backend calls), read from the
    // NODE like chartTargets() does: a generated widget whose tile cannot be
    // rebuilt (e.g. a control fed by its own API) still has one.
    const seen = new Set<string>();
    const components: Array<LabelValueOption & { disabled?: boolean }> = [];
    for (const node of nodes) {
      const tile = tiles.get(node.id);
      const widget = node.widget!;
      const componentId = String(tile?.componentId || widget.componentId || widget.build?.['componentId'] || '');
      const label = this.targetLabel(widget.layoutType, node, tile);
      // The reason goes first: the end of a long label is cut off in the list.
      if (!componentId) components.push({ label: `[generate it first] ${label}`, value: `pending:${node.id}`, disabled: true });
      else if (!seen.has(componentId) && !!seen.add(componentId)) components.push({ label, value: componentId });
    }
    for (const tile of orphanTiles) {
      if (tile.componentId && !seen.has(tile.componentId) && !!seen.add(tile.componentId)) {
        components.push({ label: this.targetLabel(tile.layoutType, undefined, tile), value: tile.componentId });
      }
    }
    // Selectable first, so the not-yet-generated ones never sit between them.
    components.sort((a, b) => Number(!!a.disabled) - Number(!!b.disabled));
    const texts = this.textTargetOptions();

    const options = numberDuplicateLabels<LabelValueOption & { disabled?: boolean }>([...components, ...texts]);
    const key = options.map((option) => `${option.value}|${option.label}|${option.disabled ? 1 : 0}`).join('~');
    if (this.targetsMemo?.key !== key) this.targetsMemo = { key, options };
    return this.targetsMemo.options;
  }

  /** "Customer Name · Dropdown — sales_data": the most specific name the
   *  component has, its type, and its dataset. */
  private targetLabel(layoutType: string, node: DesignNode | undefined, tile?: DashboardTile): string {
    const known = [...(this.layout.options?.layoutTypes ?? []), ...FALLBACK_LAYOUT_TYPES];
    // NESTED_TABLE etc. are not in every config list — never show the raw enum.
    const type = known.find((option) => option.value === layoutType)?.label
      ?? layoutType.toLowerCase().split('_').map((word) => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
    // Default titles / layer names are a type label (often of the type the
    // widget was dropped as), so none of them names the component.
    const generic = new Set(['', 'untitled', type.toLowerCase(), layoutType.toLowerCase(), ...known.map((option) => option.label.toLowerCase())]);
    const control = (tile?.dropdownControl?.widgetData ?? tile?.inputControl?.widgetData ?? {}) as Record<string, unknown>;
    // The layer name beats the design-tier label: every new dropdown's label
    // defaults to "Region", while the layer is what the user renamed.
    const name = [tile?.title, control['name'], control['label'], node?.name, node?.widget?.appearance?.['label'], tile?.dropdownControl?.field]
      .map((value) => String(value ?? '').trim())
      .find((value) => !generic.has(value.toLowerCase()));
    const payload = (tile?.payload ?? node?.widget?.build?.['payload'] ?? {}) as Record<string, unknown>;
    const datasets = payload['datasets'];
    const dataset = String(payload['dataset'] ?? (Array.isArray(datasets) ? datasets[0] ?? '' : ''));
    return `${name ? `${name} · ${type}` : type}${dataset ? ` — ${dataset}` : ''}`;
  }
  /**
   * Data-bound text boxes as targets, for dropdowns and chart interactions
   * only (sliders / inputs feed formulas, which text has none of). A text
   * whose dataset lacks the dropdown's column cannot be filtered by it, so it
   * is listed but disabled.
   */
  private textTargetOptions(): Array<LabelValueOption & { disabled?: boolean }> {
    if (this.selectedLayoutType !== 'DROPDOWN' && !this.isChartTargetLayout) return [];
    const field = this.selectedLayoutType === 'DROPDOWN' ? this.dropdownStep?.dropdownField ?? '' : '';
    return boundTextNodes(this.doc.document().pages.flatMap((page) => page.nodes)).map((node) => {
      const binding = node.dataBinding!;
      const columns = this.dataService.columns(binding.dataset);
      // A "Currency exchange" dropdown multiplies its targets rather than filtering them: any text can be one.
      const exchange = this.selected?.widget?.appearance?.['currencyExchange'] === true;
      // Missing column: still selectable, only flagged in the label.
      const missingColumn = !exchange && !!field && columns.status === 'ready' && !columns.value.includes(field);
      const aggregation = TEXT_AGGREGATIONS.find((option) => option.value === binding.aggregation)?.label ?? binding.aggregation;
      // The reason goes first: the end of a long label is cut off in the list.
      const note = missingColumn ? `[no "${field}" column] ` : '';
      return {
        label: `${note}Text · ${node.name} (${aggregation} of ${binding.field} · ${binding.dataset})`,
        value: TEXT_TARGET_PREFIX + node.id,
      };
    });
  }

  /** Text node ids currently picked in the form. */
  private get pickedTextTargets(): string[] {
    return this.selectedTargetCharts.filter((id) => id.startsWith(TEXT_TARGET_PREFIX)).map((id) => id.slice(TEXT_TARGET_PREFIX.length));
  }

  /** Mirror text targets edited from the text's side into the open form. */
  private syncTextTargets(): void {
    const stored = (this.selected?.widget?.build?.['textTargets'] as string[] | undefined) ?? [];
    if (stored.join('|') === this.pickedTextTargets.join('|')) return;
    this.selectedTargetCharts = [
      ...this.selectedTargetCharts.filter((id) => !id.startsWith(TEXT_TARGET_PREFIX)),
      ...stored.map((id) => TEXT_TARGET_PREFIX + id),
    ];
  }

  /** Every saved tile carries the form's text targets (they never reach the backend). */
  private withTextTargets(tile: DashboardTile): DashboardTile {
    return { ...tile, textTargets: this.pickedTextTargets } as DashboardTile;
  }

  get apiCallTargetOptions(): LabelValueOption[] {
    const selected = new Set(this.selectedTargetCharts);
    return [...this.targetCharts.filter((o) => selected.has(String(o.value))), ...this.targetCharts.filter((o) => !selected.has(String(o.value)))];
  }
  private targetTile(id: string): DashboardTile | undefined { return this.dashboardTiles.find((tile) => tile.componentId === id); }
  targetLabelForId(id: string): string { return this.targetCharts.find((chart) => chart.value === id)?.label || id; }
  targetLayoutTypeForId(id: string): string { return this.targetTile(id)?.layoutType || ''; }
  isInputControlTargetId(id: string): boolean { return ['SLIDER', 'INPUT_BOX'].includes(this.targetLayoutTypeForId(id)); }
  isWaterfallTarget(id: string): boolean { return this.targetLayoutTypeForId(id) === 'WATERFALL'; }
  private readonly stepsMemo = new Map<string, { key: unknown; steps: Array<{ label: string; stepType: string }> }>();
  targetSteps(id: string): Array<{ label: string; stepType: string }> {
    const tile = this.targetTile(id);
    const raw = tile?.layoutType === 'WATERFALL' && Array.isArray(tile.payload?.['steps']) ? tile.payload['steps'] as Array<Record<string, unknown>> : [];
    const memo = this.stepsMemo.get(id);
    if (memo?.key === raw) return memo.steps;
    const steps = raw.map((step) => ({ label: String(step['label'] ?? ''), stepType: String(step['stepType'] ?? '') }));
    this.stepsMemo.set(id, { key: raw, steps });
    return steps;
  }
  trackByStepLabel(index: number, step: { label: string; stepType: string }): string { return step.label || `step-${index}`; }
  targetStepFormula(id: string, label: string): string { return this.targetStepFormulas[id]?.[label] || ''; }
  setTargetStepFormula(id: string, label: string, value: string): void {
    this.targetStepFormulas = { ...this.targetStepFormulas, [id]: { ...(this.targetStepFormulas[id] || {}), [label]: value } };
  }
  targetFormulaSummary(id: string): string {
    if (!this.isWaterfallTarget(id)) return (this.targetFormulas[id] || '').trim();
    const perStep = this.targetStepFormulas[id] || {};
    return Object.keys(perStep).filter((label) => perStep[label]?.trim()).map((label) => `${label}: ${perStep[label].trim()}`).join(', ');
  }
  targetFormulasForPayload(id: string): Array<{ step?: string; formula: string; references?: Record<string, { inputTitle: string; targetLabel: string; rowLabel: string }> }> {
    if (this.isWaterfallTarget(id)) {
      return this.targetSteps(id)
        .map((step) => {
          const formula = this.targetStepFormula(id, step.label).trim();
          const references = this.referencesForFormula(id, formula, step.label);
          return { step: step.label, formula, ...(references ? { references } : {}) };
        })
        .filter((entry) => !!entry.formula);
    }
    const formula = (this.targetFormulas[id] || '').trim();
    if (!formula) return this.isInputControlTargetId(id) ? [{ formula: 'value' }] : [];
    const references = this.referencesForFormula(id, formula);
    return [{ formula, ...(references ? { references } : {}) }];
  }
  onTargetFormulaDrop(event: DragEvent, id: string, stepLabel?: string): void {
    const mapping = this.readFieldDrop(event);
    if (!mapping) return;
    this.useDroppedSingleDataset(mapping.dataset);
    if (stepLabel) this.setTargetStepFormula(id, stepLabel, this.appendFormulaToken(this.targetStepFormula(id, stepLabel), mapping.field));
    else this.targetFormulas[id] = this.appendFormulaToken(this.targetFormulas[id] || '', mapping.field);
  }
  targetReferences(id: string, step?: string): TargetFormulaReferenceEntry[] {
    return (this.targetFormulaReferences[id] || []).filter((entry) => (step ? entry.step === step : !entry.step));
  }
  addTargetReference(id: string, step?: string): void {
    this.targetFormulaReferences = {
      ...this.targetFormulaReferences,
      [id]: [...(this.targetFormulaReferences[id] || []), { token: '', inputTitle: '', targetLabel: '', rowLabel: '', step }],
    };
  }
  removeTargetReference(id: string, entry: TargetFormulaReferenceEntry): void {
    this.targetFormulaReferences = { ...this.targetFormulaReferences, [id]: (this.targetFormulaReferences[id] || []).filter((item) => item !== entry) };
  }
  isDuplicateReferenceToken(id: string, ref: TargetFormulaReferenceEntry, step?: string): boolean {
    const token = ref.token.trim();
    return !!token && this.targetReferences(id, step).some((other) => other !== ref && other.token.trim() === token);
  }
  referencesForFormula(id: string, formula: string, step?: string): Record<string, { inputTitle: string; targetLabel: string; rowLabel: string }> | undefined {
    const entries = this.targetReferences(id, step);
    if (!entries.length || !formula) return undefined;
    const tokens = new Set(Array.from(formula.matchAll(/\[([^\]]+)\]/g)).map((match) => match[1].trim()));
    const references: Record<string, { inputTitle: string; targetLabel: string; rowLabel: string }> = {};
    entries.forEach((entry) => {
      const token = entry.token.trim();
      if (token && tokens.has(token)) references[token] = { inputTitle: entry.inputTitle.trim(), targetLabel: entry.targetLabel.trim(), rowLabel: entry.rowLabel.trim() };
    });
    return Object.keys(references).length ? references : undefined;
  }
  rehydrateOwnFormulaReferences(componentId: string, references: unknown): void {
    const record = (references && typeof references === 'object' ? references : {}) as Record<string, Record<string, unknown>>;
    const rows = Object.keys(record).map((token) => ({
      token,
      inputTitle: String(record[token]?.['inputTitle'] ?? ''),
      targetLabel: String(record[token]?.['targetLabel'] ?? ''),
      rowLabel: String(record[token]?.['rowLabel'] ?? ''),
    }));
    this.targetFormulaReferences = { ...this.targetFormulaReferences, [componentId]: rows };
  }

  /** Saved dashboards a popup button / table cell can navigate to (this browser's list). */
  private navMemo: { key: string; options: LabelValueOption[] } | null = null;
  private navPinned: LabelValueOption | null = null;
  get tmNavTargetDashboards(): LabelValueOption[] {
    // Published (API) first, then this browser's saved dashboards not yet published.
    const options: LabelValueOption[] = this.apiTargetDashboards.map((d) => ({ label: d.label, value: d.value }));
    const seen = new Set(options.map((option) => option.value));
    const saved = this.persistence.consolidateSavedDashboards(readJson<SavedSelfServiceDashboard[]>(SAVED_DASHBOARDS_STORAGE_KEY, []));
    saved.forEach((dashboard) => {
      const slug = String(dashboard.config?.['dashboardId'] ?? '');
      if (slug && !seen.has(slug)) {
        seen.add(slug);
        options.push({ label: dashboard.name || slug, value: slug });
      }
    });
    if (this.navPinned && !seen.has(this.navPinned.value)) options.push(this.navPinned);
    const key = options.map((option) => option.value).join('|');
    if (this.navMemo?.key !== key) this.navMemo = { key, options };
    return this.navMemo.options;
  }
  ensureNavTargetInList(slug: string): void {
    if (this.apiTargetDashboards.some((d) => d.value === slug)) { this.navPinned = null; return; }
    this.navPinned = { label: slug.replace(/[-_]+/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()), value: slug };
  }
  /** Widgets of the chosen target dashboard (its published layout, else the saved copy). */
  private navComponentsMemo: { key: string; value: LabelValueOption[] } | null = null;
  navTargetComponentOptions(slug: string): LabelValueOption[] {
    if (!slug) { this.navComponentsMemo = { key: '', value: [] }; return []; }
    let config: Record<string, unknown> = this.apiTargetDashboards.find((d) => d.value === slug)?.layoutJson || {};
    if (!layoutHasNavTargets(config)) {
      const saved = this.persistence.consolidateSavedDashboards(readJson<SavedSelfServiceDashboard[]>(SAVED_DASHBOARDS_STORAGE_KEY, []));
      config = (saved.find((d) => String(d.config?.['dashboardId'] ?? '') === slug)?.config as Record<string, unknown>) || {};
    }
    const options = navTargetComponentOptionsFromLayout(config);
    const key = slug + '::' + options.map((o) => o.value).join('|');
    if (this.navComponentsMemo?.key !== key) this.navComponentsMemo = { key, value: options };
    return this.navComponentsMemo.value;
  }

  chartTargetsPayload(): Record<string, unknown> {
    if (!this.selectedTargetCharts.length) return {};
    return { targets: this.selectedTargetCharts.map((componentId) => ({ componentId, label: this.targetLabelForId(componentId) })) };
  }
  headerFormatPayload(): Record<string, unknown> {
    const source = this.isScatterBubbleLayout ? this.scatterBubbleStep.sbFormat : this.isPieCompositionLayout ? this.pieCompositionStep.pcFormat : this.barFormat;
    return source && Object.keys(source).length ? { format: { ...source } } : {};
  }
  apiCallConfigPayload(): Record<string, unknown> {
    const config = this.buildApiCallConfig;
    return !config?.enabled && !config?.url?.trim() ? {} : { apiConfig: sanitizeApiCallConfig(config) };
  }

  // ── Tiles: the canvas, as the steps see it ─────────────────────────────────
  get dashboardTiles(): DashboardTile[] { return this.runtime.tiles(); }
  readonly supportsDropdownFirstValue = true;
  get firstValueDropdownOptions(): LabelValueOption[] {
    const tiles = new Map(this.dashboardTiles.map((tile) => [tile.id, tile]));
    const options = numberDuplicateLabels<LabelValueOption>(widgetNodes(this.doc.document())
      .filter((node) => node.widget?.layoutType === 'DROPDOWN' && node.id !== this.selected?.id)
      // The dropdown's title (else its label / layer name), like the target list.
      .map((node) => ({ label: this.targetLabel('DROPDOWN', node, tiles.get(node.id)), value: node.id })));
    // Same array until it really changes — a fresh one each check made ng-select drop clicks.
    const key = options.map((option) => `${option.value}|${option.label}`).join('~');
    if (this.firstValueMemo?.key !== key) this.firstValueMemo = { key, options };
    return this.firstValueMemo.options;
  }
  private firstValueMemo: { key: string; options: LabelValueOption[] } | null = null;
  /** Steps write the tile they build. Before the widget's first Generate that is
   *  a live preview; from Generate on it is saved onto the widget. */
  set dashboardTiles(next: DashboardTile[]) {
    const id = this.selected?.id;
    const tile = id ? next.find((item) => item.id === id) : undefined;
    if (!tile || tile === this.runtime.tileFor(tile.id)) return;
    this.runtime.markGenerating(tile.id, false);
    if (this.persistWrites || this.selected?.widget?.build) this.built.emit({ nodeId: tile.id, tile: this.withTextTargets(tile) });
    else this.runtime.preview(tile);
  }

  newTileBase(payload: Record<string, unknown>): DashboardTile {
    return {
      id: this.selected?.id ?? `tile-${++this.componentCounter}`,
      componentId: this.componentId,
      layoutType: this.selectedLayoutType,
      title: this.title.trim() || 'Untitled',
      colSpan: 6,
      rowSpan: 8,
      payload,
    };
  }
  addTile(tile: DashboardTile): boolean {
    const updating = !!this.editingTileId;
    const id = this.selected?.id ?? tile.id;
    this.editingTileId = id;
    this.previewTileId = null;
    this.runtime.markGenerating(id, false);
    this.built.emit({ nodeId: id, tile: this.withTextTargets({ ...tile, id }) });
    return updating;
  }
  addTileFromResponse(response: Record<string, unknown> | null, payload: Record<string, unknown>): boolean {
    const base = this.newTileBase(payload);
    base.format = { ...this.defaultTileFormat(this.selectedLayoutType), ...this.barFormat };
    const step = this.activeStep;
    const tile = step?.tileFromResponse ? step.tileFromResponse(base, response, payload) : waterfallTileFromResponse(base, response, payload, this.title.trim() || undefined);
    return this.addTile(tile);
  }
  updateTileInPlace(next: DashboardTile): void {
    this.dashboardTiles = this.dashboardTiles.map((tile) => (tile.id === next.id ? next : tile)).concat(this.runtime.tileFor(next.id) ? [] : [next]);
  }
  findTileAnywhere(id: string): DashboardTile | undefined { return this.runtime.tileFor(id); }
  removePreviewTile(): void {
    if (this.previewTileId) this.runtime.forget(this.previewTileId);
    this.previewTileId = null;
  }
  invalidatePreview(): void { this.tableMatrixStep.reshapeEditingDrillTile(); }

  /**
   * A nested-table column setting from the Data tab (width, Convert currency): written as the same Design
   * tab override (`appearance.levelOverrides`) so the canvas shows it at once,
   * without Update Configuration re-running the query.
   */
  setNestedColumnOverride(levelIndex: number, field: string, patch: Record<string, unknown>): void {
    const node = this.selected ? locateNode(this.doc.nodes(), this.selected.id)?.node : null;
    if (!node?.widget || levelIndex < 0 || !field) return;
    const appearance = node.widget.appearance ?? {};
    const levels = Array.isArray(appearance['levelOverrides']) ? [...(appearance['levelOverrides'] as Array<Record<string, unknown>>)] : [];
    while (levels.length <= levelIndex) levels.push({});
    const level = { ...(levels[levelIndex] ?? {}) };
    const columns = { ...((level['columns'] as Record<string, Record<string, unknown>> | undefined) ?? {}) };
    columns[field] = { ...(columns[field] ?? {}), ...patch };
    level['columns'] = columns;
    levels[levelIndex] = level;
    this.doc.updateNode(node.id, { widget: { ...node.widget, appearance: { ...appearance, levelOverrides: levels } } }, 'Column width');
  }
  onTileFormatChange(tile: DashboardTile): void {
    const format = tile.format || {};
    if (format['title']) tile.title = format['title'] as string;
    this.stepFor(tile.layoutType)?.applyFormatToTile?.(tile, format);
    this.updateTileInPlace({ ...tile });
    this.reflowCharts();
  }
  onTileDropdownChange(tile: DashboardTile, value: string | string[], basePayload?: Record<string, unknown>): void {
    this.runtime.onDropdownChange(tile.id, value, basePayload, tile);
  }
  reloadDropdownOptions(tile: DashboardTile): void {
    this.runtime.reloadDropdownOptions(tile.id);
  }
  reflowCharts(): void { setTimeout(() => window.dispatchEvent(new Event('resize')), 60); }
  rowsFitClientChart(rows: Array<Record<string, unknown>>, data: Record<string, unknown>, fieldKey: 'category' | 'xField' | 'categoryField'): boolean {
    if (!Array.isArray(rows) || !rows.length) return true;
    const field = String((data['capabilities'] as Record<string, unknown> | undefined)?.[fieldKey] ?? '');
    return !field || rows.some((row) => field in row);
  }

  // ── Generate ───────────────────────────────────────────────────────────────
  get generateBlockers(): string[] {
    if (this.isGenerating) return [];
    const apiMissing: string[] = [];
    if (this.isChartTargetLayout && this.buildApiCallConfig.enabled) {
      if (!this.buildApiCallConfig.url.trim()) apiMissing.push('Custom API URL');
      if (this.buildApiCallConfig.method !== 'GET') {
        try { JSON.parse(this.buildApiCallConfig.bodyTemplate || '{}'); }
        catch { apiMissing.push('Valid JSON request body'); }
      }
    }
    const standsAlone = this.inputHasDataset || this.selectedLayoutType === 'DROPDOWN';
    const stepBlockers = (this.activeStep?.blockers() ?? []).filter((item) => !(item === 'At least one target chart' && standsAlone));
    return [...(this.title.trim() ? [] : ['Title']), ...stepBlockers, ...apiMissing];
  }

  /**
   * A dropdown with no targets only shows its pick ("Show the value only" on); picking targets
   * turns that off. An explicit untick (false) is kept: that dropdown filters the whole dashboard.
   */
  private syncValueOnly(): void {
    const id = this.selected?.id;
    const node = id ? locateNode(this.doc.nodes(), id)?.node : undefined;
    if (this.selectedLayoutType !== 'DROPDOWN' || !node?.widget) return;
    const current = node.widget.appearance?.['valueOnly'];
    const next = this.selectedTargetCharts.length ? (current === true ? undefined : current) : (current === false ? false : true);
    if (next === current) return;
    const appearance: Record<string, unknown> = { ...(node.widget.appearance ?? {}) };
    if (next === undefined) delete appearance['valueOnly'];
    else appearance['valueOnly'] = next;
    this.doc.updateNode(node.id, { widget: { ...node.widget, appearance } }, 'Show the value only');
  }

  /** An input box with a dataset (Fill from data, or picked here) stands on its own - no target needed. */
  private get inputHasDataset(): boolean {
    if (this.selectedLayoutType !== 'INPUT_BOX') return false;
    return !!inputDataBindingOf(this.selected) || !!this.selectedDatasets[0];
  }
  get canGenerate(): boolean { return !this.isGenerating && !this.generateBlockers.length; }
  get generateBlockerText(): string { return this.generateBlockers.length ? `Still needed: ${this.generateBlockers.join(', ')}` : ''; }

  buildPayload(): Record<string, unknown> {
    const step = this.activeStep;
    const payload = step
      ? step.buildPayload()
      : {
          componentId: this.componentId, dashboardId: this.dashboardId, componentType: this.componentType, chartType: this.selectedLayoutType,
          title: this.title.trim(), datasets: [...this.selectedDatasets], ...this.chartTargetsPayload(), ...this.headerFormatPayload(),
        };
    if (this.activeRelationHops) payload['bde_relation'] = copyRelationHops(this.relationHops);
    if (Array.isArray(payload['targets'])) {
      payload['targets'] = (payload['targets'] as Array<Record<string, unknown>>)
        .filter((target) => !String(target?.['componentId'] ?? '').startsWith(TEXT_TARGET_PREFIX));
    }
    if (this.isChartTargetLayout) payload['customApi'] = sanitizeApiCallConfig(this.buildApiCallConfig);
    return payload;
  }

  requestGenerate = (url: string, payload: Record<string, unknown>) => {
    if (!this.isChartTargetLayout || !this.buildApiCallConfig.enabled) return this.http.post<Record<string, unknown>>(url, payload);
    const nodeId = this.selected?.id;
    this.isGenerating = true;
    if (nodeId) this.runtime.markGenerating(nodeId, true);
    return this.widgetApi.resolve(url, { ...payload, componentId: this.componentId,
      customApi: sanitizeApiCallConfig(this.buildApiCallConfig) }, this.selectedLayoutType).pipe(
      map(response => {
        const result = widgetApiResult(response, this.componentId);
        if (!result) throw new Error('Custom API returned no result for this componentId.');
        return result;
      }),
      tap({ error: (error: Error) => { this.generateError = error.message || 'Unable to load the custom API data.'; } }),
      finalize(() => {
        this.isGenerating = false;
        if (nodeId) this.runtime.markGenerating(nodeId, false);
      }),
    );
  };

  /** Same flow as the Layout Builder's Generate / Update Configuration. */
  generateConfiguration(): void {
    if (!this.canGenerate || !this.selected) return;
    const preError = this.activeStep?.preGenerateError?.() ?? '';
    if (preError) { this.generateError = preError; return; }
    this.generateError = '';
    this.syncValueOnly();
    this.isGenerating = true;
    const payload = this.buildPayload();
    this.drafts.delete(this.selected.id);
    const url = resolveSelfServiceApiUrl(this.layout.apiEndpoints?.generateConfigApi, LEGACY_GENERATE_PATH);
    this.saveConfigToDb(payload);

    // From here on, a step's writes ARE the widget's new build.
    this.persistWrites = true;
    // The canvas shows "Loading data…" until the build lands (or fails).
    const generatingId = this.selected.id;
    this.runtime.markGenerating(generatingId, true);
    const step = this.activeStep;
    if (step?.generate && step.generate(payload, url)) {
      this.editingTileId = this.selected.id;
      return;
    }
    this.requestGenerate(url, payload).subscribe({
      next: (response) => {
        this.isGenerating = false;
        this.runtime.markGenerating(generatingId, false);
        const updated = this.addTileFromResponse(response, payload);
        this.ui.showSuccess(updated ? 'Chart updated.' : 'Chart generated.');
      },
      error: (error: HttpErrorResponse) => {
        this.isGenerating = false;
        this.runtime.markGenerating(generatingId, false);
        this.generateError = error.error?.message || error.message || 'Unable to generate the configuration.';
        this.ui.showError(this.generateError);
      },
    });
  }

  /** Every generated widget is saved to the backend's component store: inputs
   *  recompute their targets from it, so an unsaved chart could never be filtered. */
  private saveConfigToDb(payload: Record<string, unknown>): void {
    this.runtime.saveDefinition(this.componentId, payload).subscribe();
  }

  // ── Selected widget → form ─────────────────────────────────────────────────

  /**
   * A widget's form edits not yet applied with Update Configuration, by node id. Kept when
   * another widget is selected (e.g. a chart inside a table button's popup screen) and
   * restored on coming back, instead of the form reloading the last saved build.
   */
  private readonly drafts = new Map<string, { layoutType: string; payload: Record<string, unknown>; textTargets: string[] }>();

  /** Keeps the loaded widget's form as a draft when it differs from its saved build. */
  private stashDraft(): void {
    const id = this.loadedNodeId;
    if (!id) return;
    let payload: Record<string, unknown>;
    try {
      payload = { ...this.buildPayload(), title: this.title };
    } catch {
      return;   // a half-filled form some step cannot serialise: nothing to keep
    }
    const textTargets = this.selectedTargetCharts.filter((target) => target.startsWith(TEXT_TARGET_PREFIX));
    const saved = this.runtime.tileFor(id);
    const savedTexts = saved ? textTargetsOf(saved).map((textId) => TEXT_TARGET_PREFIX + textId) : [];
    const unchanged = !!saved?.payload && JSON.stringify(payload) === JSON.stringify({ ...saved.payload, title: this.title })
      && JSON.stringify(textTargets) === JSON.stringify(savedTexts);
    if (unchanged) this.drafts.delete(id);
    else this.drafts.set(id, { layoutType: this.selectedLayoutType, payload, textTargets });
  }

  /** Load the selected widget once per node — not on every update it receives. */
  private loadSelected(): void {
    const node = this.selected;
    if (!this.layoutReady || !node?.widget || this.loadedNodeId === node.id) return;
    this.stashDraft();
    this.loadedNodeId = node.id;
    this.persistWrites = false;
    this.resetForm(node.widget.layoutType);
    this.title = node.name || '';

    const draft = this.drafts.get(node.id)?.layoutType === node.widget.layoutType ? this.drafts.get(node.id) : undefined;
    const saved = this.runtime.tileFor(node.id);
    const tile = saved && node.widget.build ? saved : undefined;
    if (!tile && !draft) {
      this.componentId = node.widget.componentId || this.createComponentId(componentIdPrefixFor(node.widget.layoutType));
      this.preloadTextColumns();
      this.activeStep?.onLayoutActivated?.();
      return;
    }
    const payload = draft?.payload ?? tile!.payload ?? {};
    this.editingTileId = tile?.id ?? null;
    this.componentId = tile?.componentId ?? (String(payload['componentId'] ?? '')
      || node.widget.componentId || this.createComponentId(componentIdPrefixFor(node.widget.layoutType)));
    this.title = String(payload['title'] ?? tile?.title ?? node.name ?? '');
    this.relationHops = this.activeRelationHops ? copyRelationHops(payload['bde_relation']) : [];
    const datasets = Array.isArray(payload['datasets']) ? (payload['datasets'] as unknown[]).map(String) : payload['dataset'] ? [String(payload['dataset'])] : [];
    if (datasets.length) this.onSelectedDatasetsChange(datasets);
    const targets = Array.isArray(payload['targets']) ? (payload['targets'] as Array<Record<string, unknown>>) : [];
    this.selectedTargetCharts = targets.map((target) => String(target['componentId'] ?? '')).filter(Boolean);
    this.interactionsSectionOpen = this.selectedTargetCharts.length > 0;
    this.buildApiCallConfig = {
      ...defaultApiCallConfig(),
      ...(restoreApiCallConfig(payload['customApi']) ?? tile?.dropdownControl?.apiConfig ?? tile?.inputControl?.apiConfig ?? tile?.fileUploadControl?.apiConfig ?? {}),
    };
    this.activeStep?.loadFromTile(tile ? { ...tile, payload } : this.newTileBase(payload), payload);
    const texts = draft ? draft.textTargets.map((target) => target.slice(TEXT_TARGET_PREFIX.length)) : tile ? textTargetsOf(tile) : [];
    if (texts.length) {
      this.selectedTargetCharts = [
        ...this.selectedTargetCharts.filter((id) => !id.startsWith(TEXT_TARGET_PREFIX)),
        ...texts.map((id) => TEXT_TARGET_PREFIX + id),
      ];
      this.interactionsSectionOpen = true;
    }
    this.preloadTextColumns();
    this.activeStep?.onLayoutActivated?.();
  }

  /** Table types live in two forms: `nested` in its own, the rest in Table & Matrix. */
  switchTableType(type: string): void {
    const next = type === 'nested' ? 'NESTED_TABLE' : 'TABLE_MATRIX';
    if (next === 'TABLE_MATRIX') this.tableMatrixStep.tmType = type;
    if (this.selectedLayoutType !== next) {
      this.removePreviewTile();
      this.generateError = '';
      this.selectedLayoutType = next;
      this.componentId = this.createComponentId(componentIdPrefixFor(next));
      this.retypeSelectedWidget(next);
      if (this.drillDataset) this.loadColumnsForDataset(this.drillDataset);
      this.activeStep?.onLayoutActivated?.();
    }
    if (next === 'TABLE_MATRIX') this.tableMatrixStep.onTableMatrixTypeChange();
  }

  /**
   * The canvas widget takes the picked type at once — with that type's sample,
   * exactly as if it had been dropped from the palette — so the look changes
   * with the form. Generate then fills it. The previous type's build and id are
   * cleared: its definition must never be recomputed as the new type.
   */
  private retypeSelectedWidget(layoutType: string): void {
    const node = this.selected;
    if (!node?.widget || node.widget.layoutType === layoutType) return;
    this.runtime.forget(node.id);
    const retyped = withSampleData(
      { ...node.widget, layoutType, data: null, appearance: defaultAppearance(layoutType), build: undefined, componentId: undefined },
      node.id,
      node.name,
    );
    this.doc.updateNode(node.id, { widget: retyped }, 'Change table type');
  }

  /** Dropdowns: load the text datasets' columns for the "no such column" check. */
  private preloadTextColumns(): void {
    if (this.selectedLayoutType !== 'DROPDOWN') return;
    boundTextNodes(this.doc.document().pages.flatMap((page) => page.nodes))
      .forEach((node) => this.dataService.loadColumns(node.dataBinding!.dataset));
  }

  private resetForm(layoutType: string): void {
    this.selectedLayoutType = layoutType;
    this.editingTileId = null;
    this.previewTileId = null;
    this.generateError = '';
    this.selectedDatasets = [];
    this.selectedTargetCharts = [];
    this.interactionsSectionOpen = false;
    this.buildApiCallConfig = defaultApiCallConfig();
    if (this.isChartTargetLayout) this.buildApiCallConfig.bodyTemplate = '{\n  "componentId": "{componentId}",\n  "payload": "{payload}"\n}';
    this.targetFormulas = {};
    this.targetStepFormulas = {};
    this.targetFormulaReferences = {};
    this.relationHops = [];
    this.barFormat = {};
    this.applicableFormatControls(layoutType).forEach((control) => {
      if (control.default !== undefined) this.barFormat[control.key] = control.default;
    });
    this.allSteps.forEach((step) => step.reset());
  }

  private createComponentId(prefix: string): string {
    this.componentCounter += 1;
    return `${prefix}-${this.componentCounter}-${Math.floor(100 + Math.random() * 900)}`;
  }
}

/** Every data-bound text node, depth-first. */
function boundTextNodes(nodes: readonly DesignNode[]): DesignNode[] {
  const out: DesignNode[] = [];
  const walk = (list: readonly DesignNode[]): void => list.forEach((node) => {
    if (node.type === 'TEXT' && node.dataBinding?.dataset && node.dataBinding.field) out.push(node);
    if (node.children?.length) walk(node.children);
  });
  walk(nodes);
  return out;
}

/** Two components that still read the same get " #2", " #3"… so every target
 *  in the list can be told apart. */
function numberDuplicateLabels<T extends { label: string }>(options: T[]): T[] {
  const totals = new Map<string, number>();
  options.forEach((option) => totals.set(option.label, (totals.get(option.label) ?? 0) + 1));
  const counts = new Map<string, number>();
  return options.map((option) => {
    if ((totals.get(option.label) ?? 0) < 2) return option;
    const n = (counts.get(option.label) ?? 0) + 1;
    counts.set(option.label, n);
    return { ...option, label: `${option.label} #${n}` };
  });
}
